import type {
	MarketAutoPaymentOut,
	SystemAutoPaymentOut,
} from "@/api/custom_types";
import { useTranslation } from "@/hooks";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useActOnPayment } from "@/services";
import { getTimeSinceText } from "@/utils/timestamp";
import { getPaymentStateTranslation } from "@/utils/translations";
import { Button, Icon, Typography, toast } from "@st-marketplace/ui";
import { QRCodeSVG } from "qrcode.react";
import type { FC, ReactNode } from "react";

export type AnyAutoPayment = SystemAutoPaymentOut | MarketAutoPaymentOut;

const isSystemPayment = (
	payment: AnyAutoPayment,
): payment is SystemAutoPaymentOut => payment.type === "SYSTEM_AUTOMATIC";

interface UsdtTransferStepProps {
	data: AnyAutoPayment;
	onConfirmed: () => void;
	/** Rendered next to "Check again" - "Cancel" mid-flow, "Close" when resuming. */
	secondaryLabel: string;
	onSecondary: () => void;
}

const Row: FC<{ label: string; children: ReactNode }> = ({
	label,
	children,
}) => (
	<div className="flex w-full items-start justify-between gap-4">
		<Typography variant="body-sm" className="shrink-0 text-secondary-text-100">
			{label}
		</Typography>
		<div className="flex min-w-0 items-start gap-2 text-end">{children}</div>
	</div>
);

/**
 * The wallet to transfer to, and the button that asks the chain whether it
 * arrived. Shown inline in the payment flow and again from the payments list,
 * because a transfer isn't done when the dialog closes.
 */
const UsdtTransferStep: FC<UsdtTransferStepProps> = ({
	data,
	onConfirmed,
	secondaryLabel,
	onSecondary,
}) => {
	const { t, lang } = useTranslation();

	const walletAddress =
		(isSystemPayment(data)
			? data.system_wallet_address
			: data.seller_wallet_address) ?? "";
	const status = getPaymentStateTranslation(data.state, t);
	const actOnPayment = useActOnPayment();

	const copyWallet = () => {
		navigator.clipboard
			?.writeText(walletAddress)
			.then(() => toast.success(t("payment.add.autoDialog.walletCopied")))
			.catch(() => undefined);
	};

	return (
		<>
			<div className="flex flex-1 flex-col items-center gap-5 px-6 pb-2">
				<Typography variant="body-sm" className="text-center text-balance">
					{t("payment.add.autoDialog.description", {
						amount: data.claimed_amount,
						user: data.seller_name,
					})}
				</Typography>

				<div className="rounded-xl bg-white p-3">
					<QRCodeSVG value={walletAddress} size={168} marginSize={1} />
				</div>

				<button
					type="button"
					onClick={copyWallet}
					className="flex w-full items-center gap-2 rounded-xl border border-secondary-border bg-background-100 px-3 py-2 text-start hover:border-primary-border"
				>
					<Icon name="wallet-line" className="shrink-0 text-primary-100" />
					<Typography variant="body-sm" className="min-w-0 break-all" dir="ltr">
						{walletAddress}
					</Typography>
					<Icon name="file-copy-line" className="ms-auto shrink-0" />
				</button>

				<div className="flex w-full flex-col gap-2">
					<Row label={t("payment.add.autoDialog.to")}>
						<Typography variant="body-sm" className="truncate">
							{isSystemPayment(data) ? (
								data.seller_name
							) : (
								<RelationNameLink
									name={data.seller_name}
									relationId={data.relation_id}
									party="seller"
								/>
							)}
						</Typography>
					</Row>
					<Row label={t("payment.add.autoDialog.time")}>
						<Typography variant="body-sm">
							{(data.created_at &&
								getTimeSinceText(data.created_at, t, lang)) ||
								"-"}
						</Typography>
					</Row>
					<Row label={t("payment.add.autoDialog.status")}>
						<Typography variant="body-sm" className="text-primary-100">
							{status.text}
						</Typography>
					</Row>
				</div>
			</div>

			<div className="flex flex-col-reverse gap-2 border-secondary-border border-t px-4 py-4 sm:flex-row sm:justify-end">
				<Button type="button" color="secondary" size="lg" onClick={onSecondary}>
					{secondaryLabel}
				</Button>
				<Button
					type="button"
					color="primary"
					size="lg"
					className="normal-case"
					loading={actOnPayment.isPending}
					onClick={() =>
						actOnPayment.mutate(
							{
								path: { payment_id: data.id },
								body: { type_: "CHECK_AUTO_PAYMENT" },
							},
							{ onSuccess: onConfirmed },
						)
					}
				>
					{t("payment.add.autoDialog.buttonCheck")}
				</Button>
			</div>
		</>
	);
};

export default UsdtTransferStep;
